/**
 * Alshallal Pastry Company HR System - Employees List Page
 * Phase 2: User & Employee Management
 * Phase 5: Permission-based UI gating
 * Features: Table view, search, filters, pagination, sorting, bulk operations
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Plus,
  Search,
  Download,
  Trash2,
  Eye,
  Edit,
  RefreshCw,
  Users,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckSquare,
  Square,
  AlertTriangle,
  Loader2,
  FileText,
  Settings,
  Check,
  Archive
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useListViewState, useListAnchor, useDebouncedUrlText, rememberListAnchor } from '../../hooks/useListViewState';
import { usePermission, Permission } from '@/contexts/PermissionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status badge styles
const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  on_leave: 'bg-blue-100 text-blue-800',
  suspended: 'bg-amber-100 text-amber-800',
  terminated: 'bg-red-100 text-red-800',
  resigned: 'bg-slate-100 text-slate-800',
};

const STATUS_LABELS = {
  active: { en: 'Active', ar: 'نشط' },
  on_leave: { en: 'On Leave', ar: 'في إجازة' },
  suspended: { en: 'Suspended', ar: 'موقوف' },
  terminated: { en: 'Terminated', ar: 'منتهي' },
  resigned: { en: 'Resigned', ar: 'مستقيل' },
};

const NATIONALITY_LABELS = {
  saudi: { en: 'Saudi', ar: 'سعودي' },
  non_saudi: { en: 'Non-Saudi', ar: 'غير سعودي' },
};

const EmployeesListPage = () => {
  const navigate = useNavigate();
  const { token, hasRole, user } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const { hasPermission, hasAnyPermission } = usePermission();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Page, filters and sorting live in the URL, so opening an employee and
  // coming back returns to the same page instead of restarting at page 1.
  const [view, setView] = useListViewState('employees', {
    page: 1,
    search: '',
    department: 'all',
    departmentId: 'all',
    status: 'all',
    nationality: 'all',
    sort: 'created_at',
    dir: 'desc',
  });
  const {
    page,
    search: searchQuery,
    department: departmentFilter,
    departmentId: departmentIdFilter,
    status: statusFilter,
    nationality: nationalityFilter,
    sort: sortField,
    dir: sortDirection,
  } = view;

  // Changing any filter goes back to page 1 — page 17 of the old result set
  // means nothing once the result set changes.
  const setPage = useCallback((next) => setView({ page: typeof next === 'function' ? next(page) : next }), [setView, page]);
  const setSearchQuery = useCallback((value) => setView({ search: value, page: 1 }), [setView]);
  const [searchInput, setSearchInput] = useDebouncedUrlText(searchQuery, setSearchQuery);
  const setDepartmentFilter = useCallback((value) => setView({ department: value, page: 1 }), [setView]);
  const setDepartmentIdFilter = useCallback((value) => setView({ departmentId: value, page: 1 }), [setView]);
  const setStatusFilter = useCallback((value) => setView({ status: value, page: 1 }), [setView]);
  const setNationalityFilter = useCallback((value) => setView({ nationality: value, page: 1 }), [setView]);

  // Filters
  const [departments, setDepartments] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);  // Phase 18.3.1: Managed departments

  // Selection for bulk operations
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Delete dialogs
  const [deleteDialog, setDeleteDialog] = useState({ open: false, employee: null });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
  
  // Phase 21: Bulk archive state
  const [bulkArchiveDialog, setBulkArchiveDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [bulkArchiveResult, setBulkArchiveResult] = useState(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  
  // Phase 18.8.1: Custom export with field selection
  const [exportDialog, setExportDialog] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [fieldCategories, setFieldCategories] = useState([]);
  const [selectedExportFields, setSelectedExportFields] = useState(new Set());
  const [loadingFields, setLoadingFields] = useState(false);

  // Phase 18.8.1: Expanded export available for HR Manager, HR Admin, Super Admin
  const canExpandedExport = hasPermission(Permission.EMPLOYEE_EXPORT);

  const pageSize = 20;

  // Phase 5: Permission-based access control
  const canCreate = hasPermission(Permission.EMPLOYEE_CREATE);
  const canEdit = hasPermission(Permission.EMPLOYEE_EDIT);
  const canDelete = hasPermission(Permission.EMPLOYEE_DELETE);
  const canArchive = hasPermission(Permission.USER_ARCHIVE);  // Phase 21: Archive requires specific permission
  const canExport = hasPermission(Permission.EMPLOYEE_EXPORT);
  const canManageDepartments = hasAnyPermission([Permission.EMPLOYEE_CREATE, Permission.EMPLOYEE_EDIT]);  // Phase 18.3.1

  // Fetch departments (legacy text-based)
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API_URL}/employees/departments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDepartments(response.data.departments || []);
      } catch (err) {
        console.error('Failed to fetch departments:', err);
      }
    };
    fetchDepartments();
  }, [token]);

  // Phase 18.3.1: Fetch managed departments for HR Admin/Super Admin
  useEffect(() => {
    const fetchManagedDepartments = async () => {
      if (!canManageDepartments) return;
      
      try {
        const response = await axios.get(`${API_URL}/departments/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDepartmentsList(response.data.departments || []);
      } catch (err) {
        console.error('Failed to fetch managed departments:', err);
      }
    };
    fetchManagedDepartments();
  }, [token, canManageDepartments]);

  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (searchQuery) params.append('search', searchQuery);
      if (departmentFilter && departmentFilter !== 'all') params.append('department', departmentFilter);
      if (departmentIdFilter && departmentIdFilter !== 'all') params.append('department_id', departmentIdFilter);  // Phase 18.3.1
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (nationalityFilter && nationalityFilter !== 'all') params.append('nationality', nationalityFilter);

      const response = await axios.get(`${API_URL}/employees/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let emps = response.data.employees || [];
      
      // Client-side sorting (backend sorting can be added later)
      emps = sortEmployees(emps, sortField, sortDirection);
      
      setEmployees(emps);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
      
      // Clear selection when data changes
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to fetch employees:', err);
      setError(err.response?.data?.detail?.error || t('error_occurred'));
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, departmentFilter, departmentIdFilter, statusFilter, nationalityFilter, sortField, sortDirection, token, t]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Scroll back to the employee that was opened, once the rows are on screen.
  const anchoredEmployeeId = useListAnchor('employees', !loading && employees.length > 0);

  // Sort employees client-side
  const sortEmployees = (emps, field, direction) => {
    return [...emps].sort((a, b) => {
      let aVal, bVal;
      
      switch (field) {
        case 'name':
          aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
          bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'hire_date':
          aVal = a.hire_date ? new Date(a.hire_date) : new Date(0);
          bVal = b.hire_date ? new Date(b.hire_date) : new Date(0);
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at) : new Date(0);
          bVal = b.created_at ? new Date(b.created_at) : new Date(0);
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Remember the row so the list can scroll back to it on return.
  const openEmployee = (employeeId, suffix = '') => {
    rememberListAnchor('employees', employeeId);
    navigate(`/employees/${employeeId}${suffix}`);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setView({ dir: sortDirection === 'asc' ? 'desc' : 'asc' });
    } else {
      setView({ sort: field, dir: 'asc' });
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ms-1 text-slate-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ms-1 text-blue-600" />
      : <ArrowDown className="h-3 w-3 ms-1 text-blue-600" />;
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === employees.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(employees.map(e => e.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Phase 18.8.1: Fetch available export fields
  const fetchExportFields = async () => {
    try {
      setLoadingFields(true);
      const response = await axios.get(`${API_URL}/employees/export-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableFields(response.data.fields || []);
      setFieldCategories(response.data.categories || []);
      
      // Pre-select basic fields by default
      const basicFields = (response.data.fields || [])
        .filter(f => ['basic', 'employment'].includes(f.category))
        .map(f => f.key);
      setSelectedExportFields(new Set(basicFields));
    } catch (err) {
      console.error('Failed to fetch export fields:', err);
    } finally {
      setLoadingFields(false);
    }
  };

  // Phase 18.8.1: Handle custom export with selected fields
  const handleCustomExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (departmentFilter && departmentFilter !== 'all') params.append('department', departmentFilter);
      if (departmentIdFilter && departmentIdFilter !== 'all') params.append('department_id', departmentIdFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      // Add selected fields
      const fieldsArray = Array.from(selectedExportFields);
      if (fieldsArray.length > 0) {
        params.append('fields', fieldsArray.join(','));
      }

      const response = await axios.get(`${API_URL}/employees/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employees_export_custom_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportDialog(false);
    } catch (err) {
      console.error('Export failed:', err);
      setError(isRTL ? 'فشل التصدير' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Phase 18.8.1: Toggle field selection
  const toggleFieldSelection = (fieldKey) => {
    const newSelected = new Set(selectedExportFields);
    if (newSelected.has(fieldKey)) {
      newSelected.delete(fieldKey);
    } else {
      newSelected.add(fieldKey);
    }
    setSelectedExportFields(newSelected);
  };

  // Phase 18.8.1: Select/deselect all fields in a category
  const toggleCategory = (categoryKey) => {
    const categoryFields = availableFields.filter(f => f.category === categoryKey).map(f => f.key);
    const allSelected = categoryFields.every(f => selectedExportFields.has(f));
    
    const newSelected = new Set(selectedExportFields);
    categoryFields.forEach(f => {
      if (allSelected) {
        newSelected.delete(f);
      } else {
        newSelected.add(f);
      }
    });
    setSelectedExportFields(newSelected);
  };

  // Export employees
  // Phase 18.6.x: Added expanded parameter for full profile export
  const handleExport = async (expanded = false) => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (departmentFilter && departmentFilter !== 'all') params.append('department', departmentFilter);
      if (departmentIdFilter && departmentIdFilter !== 'all') params.append('department_id', departmentIdFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      // Phase 18.6.x: Add expanded flag for full profile export
      if (expanded) params.append('expanded', 'true');

      const response = await axios.get(`${API_URL}/employees/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Download file - filename reflects export type
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const exportType = expanded ? 'expanded_' : '';
      link.setAttribute('download', `employees_export_${exportType}${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setError(isRTL ? 'فشل التصدير' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Single delete employee
  const handleDelete = async () => {
    if (!deleteDialog.employee || deleteReason.length < 10) return;

    try {
      setDeleting(true);
      await axios.delete(
        `${API_URL}/employees/${deleteDialog.employee.id}?reason=${encodeURIComponent(deleteReason)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDeleteDialog({ open: false, employee: null });
      setDeleteReason('');
      fetchEmployees();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.response?.data?.detail?.error || t('error_occurred'));
    } finally {
      setDeleting(false);
    }
  };

  // Bulk delete employees
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0 || deleteReason.length < 10) return;

    try {
      setDeleting(true);
      setBulkDeleteResult(null);

      const response = await axios.post(
        `${API_URL}/employees/bulk-delete`,
        {
          employee_ids: Array.from(selectedIds),
          reason: deleteReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBulkDeleteResult(response.data);
      
      // If all succeeded, close dialog and refresh
      if (response.data.failed_count === 0) {
        setTimeout(() => {
          setBulkDeleteDialog(false);
          setDeleteReason('');
          setBulkDeleteResult(null);
          setSelectedIds(new Set());
          fetchEmployees();
        }, 2000);
      }
    } catch (err) {
      console.error('Bulk delete failed:', err);
      setError(err.response?.data?.detail?.error || t('error_occurred'));
    } finally {
      setDeleting(false);
    }
  };

  const closeBulkDeleteDialog = () => {
    setBulkDeleteDialog(false);
    setDeleteReason('');
    setBulkDeleteResult(null);
    if (bulkDeleteResult?.deleted_count > 0) {
      setSelectedIds(new Set());
      fetchEmployees();
    }
  };
  
  // Phase 21: Bulk archive employees (to Repository)
  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return;

    try {
      setArchiving(true);
      setBulkArchiveResult(null);

      const response = await axios.post(
        `${API_URL}/employees/bulk-archive`,
        {
          ids: Array.from(selectedIds),
          reason: archiveReason || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBulkArchiveResult(response.data);
      
      // If all succeeded, close dialog and refresh
      if (response.data.success && response.data.failed?.length === 0) {
        setTimeout(() => {
          setBulkArchiveDialog(false);
          setArchiveReason('');
          setBulkArchiveResult(null);
          setSelectedIds(new Set());
          fetchEmployees();
        }, 2000);
      }
    } catch (err) {
      console.error('Bulk archive failed:', err);
      setError(err.response?.data?.detail?.error || t('error_occurred'));
    } finally {
      setArchiving(false);
    }
  };

  const closeBulkArchiveDialog = () => {
    setBulkArchiveDialog(false);
    setArchiveReason('');
    setBulkArchiveResult(null);
    if (bulkArchiveResult?.archived?.length > 0) {
      setSelectedIds(new Set());
      fetchEmployees();
    }
  };

  const getStatusLabel = (status) => {
    const labels = STATUS_LABELS[status];
    return labels ? (language === 'ar' ? labels.ar : labels.en) : status;
  };

  const getNationalityLabel = (nat) => {
    const labels = NATIONALITY_LABELS[nat];
    return labels ? (language === 'ar' ? labels.ar : labels.en) : nat;
  };

  const clearFilters = () => {
    setView({
      search: '',
      department: 'all',
      departmentId: 'all',
      status: 'all',
      nationality: 'all',
      sort: 'created_at',
      dir: 'desc',
      page: 1,
    });
  };

  const hasActiveFilters = searchQuery || departmentFilter !== 'all' || statusFilter !== 'all' || nationalityFilter !== 'all';

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="employees-list-page">
      {/* Header - responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t('employees')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            {isRTL ? `${total} موظف` : `${total} employees`}
            {selectedIds.size > 0 && (
              <span className="ms-2 text-blue-600 font-medium">
                ({isRTL ? `${selectedIds.size} محدد` : `${selectedIds.size} selected`})
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Phase 21: Bulk Archive Button - Only show when items selected and super admin */}
          {canArchive && selectedIds.size > 0 && (
            <Button
              variant="outline"
              onClick={() => setBulkArchiveDialog(true)}
              data-testid="bulk-archive-btn"
              size="sm"
              className="sm:size-default"
            >
              <Archive className="h-4 w-4 me-2" />
              {isRTL ? `أرشفة (${selectedIds.size})` : `Archive (${selectedIds.size})`}
            </Button>
          )}
          {/* Bulk Delete Button - Only show when items selected */}
          {canDelete && selectedIds.size > 0 && (
            <Button
              variant="destructive"
              onClick={() => setBulkDeleteDialog(true)}
              data-testid="bulk-delete-btn"
              size="sm"
              className="sm:size-default"
            >
              <Trash2 className="h-4 w-4 me-2" />
              {isRTL ? `حذف (${selectedIds.size})` : `Delete (${selectedIds.size})`}
            </Button>
          )}
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={exporting}
                  data-testid="export-btn"
                  size="sm"
                  className="sm:size-default"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 sm:me-2" />
                  )}
                  <span className="hidden sm:inline">{t('export')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                {/* Phase 18.8.1: Custom export with field selection */}
                {canExpandedExport && (
                  <DropdownMenuItem 
                    onClick={() => {
                      fetchExportFields();
                      setExportDialog(true);
                    }}
                    data-testid="export-custom-btn"
                  >
                    <Settings className="h-4 w-4 me-2" />
                    {isRTL ? 'تصدير مخصص (اختر الحقول)' : 'Custom Export (Select Fields)'}
                  </DropdownMenuItem>
                )}
                {/* Phase 18.6.x: Expanded export (full profile) - default for HR Admin/Super Admin */}
                {canExpandedExport && (
                  <DropdownMenuItem 
                    onClick={() => handleExport(true)}
                    data-testid="export-expanded-btn"
                  >
                    <FileText className="h-4 w-4 me-2" />
                    {isRTL ? 'تصدير كامل (جميع الحقول)' : 'Full Export (All Fields)'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={() => handleExport(false)}
                  data-testid="export-standard-btn"
                >
                  <Download className="h-4 w-4 me-2" />
                  {isRTL ? 'تصدير أساسي' : 'Standard Export'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreate && (
            <Button
              onClick={() => navigate('/employees/new')}
              data-testid="create-employee-btn"
              size="sm"
              className="sm:size-default"
            >
              <Plus className="h-4 w-4 sm:me-2" />
              <span className="hidden sm:inline">{isRTL ? 'إضافة موظف' : 'Add Employee'}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Filters - responsive */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[280px]">
              <div className="relative">
                <Search className={cn(
                  "h-4 w-4 absolute top-1/2 -translate-y-1/2 text-slate-400",
                  isRTL ? "right-3" : "left-3"
                )} />
                <Input
                  placeholder={isRTL 
                    ? 'بحث بالاسم، رقم الموظف، الهوية، البريد، الجوال...' 
                    : 'Search by name, employee #, ID/Iqama, email, mobile...'}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className={cn("h-10", isRTL ? "pr-10" : "pl-10")}
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Department filter (legacy text-based) */}
            <div className="w-48">
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger data-testid="department-filter">
                  <SelectValue placeholder={isRTL ? 'كل الأقسام' : 'All Departments'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'كل الأقسام' : 'All Departments'}</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phase 18.3.1: Managed Department filter (HR Admin/Super Admin only) */}
            {canManageDepartments && departmentsList.length > 0 && (
              <div className="w-52">
                <Select value={departmentIdFilter} onValueChange={setDepartmentIdFilter}>
                  <SelectTrigger data-testid="department-id-filter">
                    <SelectValue placeholder={isRTL ? 'القسم المنظم' : 'Org Department'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{isRTL ? 'كل الأقسام المنظمة' : 'All Org Departments'}</SelectItem>
                    {departmentsList.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {isRTL && dept.name_ar ? dept.name_ar : dept.name_en}
                        {dept.code && ` (${dept.code})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Status filter */}
            <div className="w-40">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder={isRTL ? 'كل الحالات' : 'All Status'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'كل الحالات' : 'All Status'}</SelectItem>
                  <SelectItem value="active">{getStatusLabel('active')}</SelectItem>
                  <SelectItem value="on_leave">{getStatusLabel('on_leave')}</SelectItem>
                  <SelectItem value="suspended">{getStatusLabel('suspended')}</SelectItem>
                  <SelectItem value="terminated">{getStatusLabel('terminated')}</SelectItem>
                  <SelectItem value="resigned">{getStatusLabel('resigned')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Nationality filter */}
            <div className="w-36">
              <Select value={nationalityFilter} onValueChange={setNationalityFilter}>
                <SelectTrigger data-testid="nationality-filter">
                  <SelectValue placeholder={isRTL ? 'الجنسية' : 'Nationality'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  <SelectItem value="saudi">{getNationalityLabel('saudi')}</SelectItem>
                  <SelectItem value="non_saudi">{getNationalityLabel('non_saudi')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Refresh and Clear */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchEmployees}
                disabled={loading}
                title={isRTL ? 'تحديث' : 'Refresh'}
              >
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  {isRTL ? 'مسح الفلاتر' : 'Clear filters'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Employees table */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-beige-60/60 backdrop-blur-md border-b border-border sticky top-0 z-10">
                <tr>
                  {/* Select all checkbox */}
                  {canDelete && employees.length > 0 && (
                    <th className="px-4 py-3 w-12">
                      <Checkbox
                        checked={selectedIds.size === employees.length && employees.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                  )}
                  <th 
                    className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-brand-black/60 cursor-pointer hover:bg-brand-beige-60 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <span className="flex items-center">
                      {isRTL ? 'الموظف' : 'Employee'}
                      {getSortIcon('name')}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-brand-black/60">
                    {isRTL ? 'القسم' : 'Department'}
                  </th>
                  <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-brand-black/60">
                    {isRTL ? 'المسمى الوظيفي' : 'Job Title'}
                  </th>
                  <th 
                    className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-brand-black/60 cursor-pointer hover:bg-brand-beige-60 transition-colors"
                    onClick={() => handleSort('hire_date')}
                  >
                    <span className="flex items-center">
                      {isRTL ? 'تاريخ التعيين' : 'Hire Date'}
                      {getSortIcon('hire_date')}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-brand-black/60">
                    {t('status')}
                  </th>
                  <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-brand-black/60">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-black/5">
                {loading ? (
                  <tr>
                    <td colSpan={canDelete ? 7 : 6} className="px-4 py-12 text-center">
                      <RefreshCw className="h-6 w-6 animate-spin text-brand-orange mx-auto" />
                      <p className="text-muted-foreground mt-2">{t('loading')}</p>
                    </td>
                  </tr>
                ) : employees.length === 0 ? (
                  <tr>
                    <td colSpan={canDelete ? 7 : 6} className="px-4 py-12 text-center">
                      <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 font-medium">
                        {hasActiveFilters 
                          ? (isRTL ? 'لا توجد نتائج مطابقة' : 'No matching results') 
                          : t('no_data')}
                      </p>
                      {hasActiveFilters ? (
                        <Button
                          variant="link"
                          onClick={clearFilters}
                          className="mt-2"
                        >
                          {isRTL ? 'مسح الفلاتر' : 'Clear filters'}
                        </Button>
                      ) : canCreate && (
                        <Button
                          variant="link"
                          onClick={() => navigate('/employees/new')}
                          className="mt-2"
                        >
                          {isRTL ? 'إضافة أول موظف' : 'Add first employee'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ) : (
                  employees.map((emp) => (
                    <tr
                      key={emp.id}
                      className={cn(
                        "transition-colors duration-150 ease-out",
                        "hover:bg-brand-beige/30",
                        selectedIds.has(emp.id) && "bg-[hsl(202_56%_83%/0.40)] hover:bg-[hsl(202_56%_83%/0.50)]",
                        anchoredEmployeeId === emp.id && "row-return-flash"
                      )}
                      data-testid={`employee-row-${emp.id}`}
                      data-row-anchor={emp.id}
                    >
                      {/* Row checkbox */}
                      {canDelete && (
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(emp.id)}
                            onCheckedChange={() => toggleSelect(emp.id)}
                            data-testid={`select-${emp.id}`}
                          />
                        </td>
                      )}
                      <td 
                        className="px-4 py-3 cursor-pointer"
                        onClick={() => openEmployee(emp.id)}
                      >
                        <div>
                          <p className="font-medium text-slate-900">
                            {isRTL && emp.first_name_ar
                              ? `${emp.first_name_ar} ${emp.last_name_ar || ''}`
                              : `${emp.first_name} ${emp.last_name}`
                            }
                          </p>
                          <p className="text-sm text-slate-500">
                            {emp.employee_number || emp.national_id_masked || emp.iqama_masked}
                          </p>
                        </div>
                      </td>
                      <td 
                        className="px-4 py-3 text-sm text-slate-600 cursor-pointer"
                        onClick={() => openEmployee(emp.id)}
                      >
                        {isRTL && emp.department_ar ? emp.department_ar : emp.department}
                      </td>
                      <td 
                        className="px-4 py-3 text-sm text-slate-600 cursor-pointer"
                        onClick={() => openEmployee(emp.id)}
                      >
                        {isRTL && emp.job_title_ar ? emp.job_title_ar : emp.job_title}
                      </td>
                      <td 
                        className="px-4 py-3 text-sm text-slate-600 cursor-pointer"
                        onClick={() => openEmployee(emp.id)}
                      >
                        {emp.hire_date ? format(new Date(emp.hire_date), 'PP') : '-'}
                      </td>
                      <td 
                        className="px-4 py-3 cursor-pointer"
                        onClick={() => openEmployee(emp.id)}
                      >
                        <Badge className={STATUS_STYLES[emp.status] || STATUS_STYLES.active}>
                          {getStatusLabel(emp.status)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`actions-${emp.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align={isRTL ? "start" : "end"}>
                            <DropdownMenuItem onClick={() => openEmployee(emp.id)}>
                              <Eye className="h-4 w-4 me-2" />
                              {t('view')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEmployee(emp.id, '?tab=documents')}>
                              <FileText className="h-4 w-4 me-2" />
                              {isRTL ? 'المستندات' : 'Documents'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEmployee(emp.id, '/edit')}>
                              <Edit className="h-4 w-4 me-2" />
                              {t('edit')}
                            </DropdownMenuItem>
                            {canDelete && (
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => setDeleteDialog({ open: true, employee: emp })}
                              >
                                <Trash2 className="h-4 w-4 me-2" />
                                {t('delete')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            {isRTL
              ? `صفحة ${page} من ${totalPages}`
              : `Page ${page} of ${totalPages}`
            }
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Single delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, employee: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {isRTL ? 'حذف الموظف' : 'Delete Employee'}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? `هل أنت متأكد من حذف ${deleteDialog.employee?.first_name} ${deleteDialog.employee?.last_name}؟ سيتم تعطيل سجل الموظف.`
                : `Are you sure you want to delete ${deleteDialog.employee?.first_name} ${deleteDialog.employee?.last_name}? The employee record will be deactivated.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="delete-reason">
                {isRTL ? 'سبب الحذف (مطلوب)' : 'Reason for deletion (required)'}
              </Label>
              <Textarea
                id="delete-reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب الحذف (10 أحرف على الأقل)...' : 'Enter reason for deletion (at least 10 characters)...'}
                rows={3}
                data-testid="delete-reason-input"
              />
              {deleteReason.length > 0 && deleteReason.length < 10 && (
                <p className="text-sm text-red-500">
                  {isRTL ? `${10 - deleteReason.length} أحرف متبقية` : `${10 - deleteReason.length} more characters needed`}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, employee: null })}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteReason.length < 10 || deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? t('loading') : t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation dialog */}
      <Dialog open={bulkDeleteDialog} onOpenChange={(open) => !open && closeBulkDeleteDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {isRTL ? 'حذف جماعي' : 'Bulk Delete'}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? `أنت على وشك حذف ${selectedIds.size} موظف. هذا الإجراء سيعطل سجلاتهم.`
                : `You are about to delete ${selectedIds.size} employees. This action will deactivate their records.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Warning message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <p className="font-medium mb-1">
              {isRTL ? 'ملاحظة:' : 'Note:'}
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>{isRTL ? 'لا يمكن حذف الحسابات المحمية (المرتبطة بمسؤول النظام)' : 'Protected accounts (linked to Super Admin) cannot be deleted'}</li>
              <li>{isRTL ? 'لا يمكنك حذف سجل موظفك الخاص' : 'You cannot delete your own employee record'}</li>
            </ul>
          </div>

          {/* Bulk delete result */}
          {bulkDeleteResult && (
            <div className={cn(
              "rounded-lg p-3 text-sm",
              bulkDeleteResult.failed_count > 0 
                ? "bg-amber-50 border border-amber-200 text-amber-800"
                : "bg-green-50 border border-green-200 text-green-800"
            )}>
              <p className="font-medium mb-2">
                {isRTL ? 'نتيجة العملية:' : 'Operation Result:'}
              </p>
              <p>
                {isRTL 
                  ? `تم الحذف: ${bulkDeleteResult.deleted_count} | فشل: ${bulkDeleteResult.failed_count}`
                  : `Deleted: ${bulkDeleteResult.deleted_count} | Failed: ${bulkDeleteResult.failed_count}`
                }
              </p>
              {bulkDeleteResult.failed && bulkDeleteResult.failed.length > 0 && (
                <ul className="mt-2 list-disc list-inside text-xs">
                  {bulkDeleteResult.failed.map((f, i) => (
                    <li key={i}>{f.id.slice(0, 8)}... - {f.reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!bulkDeleteResult && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="bulk-delete-reason">
                  {isRTL ? 'سبب الحذف الجماعي (مطلوب)' : 'Reason for bulk deletion (required)'}
                </Label>
                <Textarea
                  id="bulk-delete-reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder={isRTL ? 'أدخل سبب الحذف (10 أحرف على الأقل)...' : 'Enter reason for deletion (at least 10 characters)...'}
                  rows={3}
                  data-testid="bulk-delete-reason-input"
                />
                {deleteReason.length > 0 && deleteReason.length < 10 && (
                  <p className="text-sm text-red-500">
                    {isRTL ? `${10 - deleteReason.length} أحرف متبقية` : `${10 - deleteReason.length} more characters needed`}
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeBulkDeleteDialog}>
              {bulkDeleteResult ? (isRTL ? 'إغلاق' : 'Close') : t('cancel')}
            </Button>
            {!bulkDeleteResult && (
              <Button
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={deleteReason.length < 10 || deleting}
                data-testid="confirm-bulk-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                    {isRTL ? 'جاري الحذف...' : 'Deleting...'}
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 me-2" />
                    {isRTL ? `حذف ${selectedIds.size} موظف` : `Delete ${selectedIds.size} employees`}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 21: Bulk Archive Dialog */}
      <Dialog open={bulkArchiveDialog} onOpenChange={(open) => !open && closeBulkArchiveDialog()}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              {isRTL ? 'أرشفة الموظفين' : 'Archive Employees'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? `سيتم أرشفة ${selectedIds.size} موظف(ين). يمكن استعادتهم من المستودع.`
                : `${selectedIds.size} employee(s) will be archived. They can be restored from Repository.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {bulkArchiveResult ? (
              <div className="space-y-3">
                {bulkArchiveResult.archived?.length > 0 && (
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="h-5 w-5" />
                    <span>
                      {isRTL 
                        ? `تم أرشفة ${bulkArchiveResult.archived.length} موظف`
                        : `${bulkArchiveResult.archived.length} employees archived`}
                    </span>
                  </div>
                )}
                {bulkArchiveResult.skipped?.length > 0 && (
                  <div className="text-amber-600">
                    <p className="font-medium">
                      {isRTL ? 'تم تخطي:' : 'Skipped:'}
                    </p>
                    <ul className="text-sm list-disc list-inside">
                      {bulkArchiveResult.skipped.map((item, idx) => (
                        <li key={idx}>{item.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    {isRTL 
                      ? 'الموظفين المؤرشفين وحساباتهم المرتبطة سيتم تعطيلها. يمكن استعادتهم من صفحة المستودع.'
                      : 'Archived employees and their linked accounts will be disabled. They can be restored from the Repository page.'
                    }
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {isRTL ? 'السبب (اختياري)' : 'Reason (optional)'}
                  </label>
                  <Input
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                    placeholder={isRTL ? 'أدخل سبب الأرشفة...' : 'Enter reason for archiving...'}
                  />
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeBulkArchiveDialog}>
              {bulkArchiveResult ? (isRTL ? 'إغلاق' : 'Close') : (isRTL ? 'إلغاء' : 'Cancel')}
            </Button>
            {!bulkArchiveResult && (
              <Button onClick={handleBulkArchive} disabled={archiving}>
                {archiving ? (
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                ) : (
                  <Archive className="h-4 w-4 me-2" />
                )}
                {isRTL ? `أرشفة ${selectedIds.size} موظف` : `Archive ${selectedIds.size} employees`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 18.8.1: Custom Export Dialog */}
      <Dialog open={exportDialog} onOpenChange={setExportDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {isRTL ? 'تصدير مخصص - اختر الحقول' : 'Custom Export - Select Fields'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'اختر الحقول التي تريد تضمينها في التقرير. الحقول المخصصة المحددة من قبل مدير الموارد البشرية ستظهر أيضاً.'
                : 'Choose which fields to include in the report. Custom fields defined by HR Manager will also appear.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {loadingFields ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="space-y-6">
                {fieldCategories.map(category => {
                  const categoryFields = availableFields.filter(f => f.category === category.key);
                  if (categoryFields.length === 0) return null;
                  
                  const allSelected = categoryFields.every(f => selectedExportFields.has(f.key));
                  const someSelected = categoryFields.some(f => selectedExportFields.has(f.key));
                  
                  return (
                    <div key={category.key} className="border rounded-lg p-4">
                      <div 
                        className="flex items-center gap-3 cursor-pointer mb-3"
                        onClick={() => toggleCategory(category.key)}
                      >
                        <div className={cn(
                          "w-5 h-5 border rounded flex items-center justify-center",
                          allSelected ? "bg-blue-600 border-blue-600" : someSelected ? "bg-blue-100 border-blue-600" : "border-slate-300"
                        )}>
                          {allSelected && <Check className="h-3 w-3 text-white" />}
                          {someSelected && !allSelected && <div className="w-2 h-2 bg-blue-600 rounded-sm" />}
                        </div>
                        <span className="font-semibold text-slate-900">
                          {isRTL ? category.label_ar : category.label_en}
                        </span>
                        <Badge variant="outline" className="ms-auto">
                          {categoryFields.filter(f => selectedExportFields.has(f.key)).length}/{categoryFields.length}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {categoryFields.map(field => (
                          <label 
                            key={field.key}
                            className={cn(
                              "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 text-sm",
                              selectedExportFields.has(field.key) && "bg-blue-50"
                            )}
                          >
                            <Checkbox
                              checked={selectedExportFields.has(field.key)}
                              onCheckedChange={() => toggleFieldSelection(field.key)}
                            />
                            <span className={cn(
                              field.is_sensitive && "text-amber-700",
                              field.is_custom && "text-purple-700"
                            )}>
                              {isRTL ? field.label_ar : field.label_en}
                              {field.is_custom && (
                                <span className="ms-1 text-xs text-purple-500">(مخصص)</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center gap-2 me-auto text-sm text-slate-600">
              <Check className="h-4 w-4" />
              {isRTL 
                ? `${selectedExportFields.size} حقل محدد`
                : `${selectedExportFields.size} fields selected`}
            </div>
            <Button variant="outline" onClick={() => setExportDialog(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleCustomExport}
              disabled={exporting || selectedExportFields.size === 0}
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {isRTL ? 'جاري التصدير...' : 'Exporting...'}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 me-2" />
                  {isRTL ? 'تصدير' : 'Export'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesListPage;
